import { lazy, useEffect } from 'react';
import { wrapCreateBrowserRouterV6 } from '@sentry/react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  InactivityTimeout,
  RootRedirect,
  SuspenseLoader,
} from '@router/components';
import { FullScreenLoader } from '@router/components/SuspenseLoader';
import {
  bookingConfirmationLoader,
  completeBookingLoader,
  completeProfileLoader,
  confirmNumberLoader,
  createPasswordLoader,
  emailLoader,
  idVerificationLoader,
  intakeQuizLoader,
  rootLoader,
  scheduleLoader,
  servicesLoader,
} from '../loaders';
import { appointmentTimePreferenceLoader } from '@router/loaders/appointmentTimePreferenceLoader/appointmentTimePreferenceLoader';
import { referralLoader } from '@router/loaders/referralLoader';
import {
  authRoutes,
  drugScreenRoutes,
  intakeFormRoutes,
  refillsRoutes,
} from '../routes';
import {
  BOOKING_CONFIRMATION_ROUTE_PATHNAME,
  COMPLETE_BOOKING_ROUTE_PATHNAME,
  COMPLETE_PROFILE_ROUTE_PATHNAME,
  CONFIRM_YOUR_NUMBER_ROUTE_PATHNAME,
  CREATE_PASSWORD_ROUTE_PATHNAME,
  DEV_TOOLS_SANDBOX_ROUTE_PATHNAME,
  EMAIL_ROUTE_PATHNAME,
  HOME_ROUTE_PATHNAME,
  ID_VERIFICATION_ROUTE_PATHNAME,
  PHONE_ROUTE_PATHNAME,
  REFERRAL_ROUTE_PATHNAME,
  RESCHEDULE_ROUTE_VISIT_TYPE_ID_PARAM,
  SCHEDULE_ROUTE_VISIT_TYPE_ID_PARAM,
  SERVICES_ROUTE_SERVICE_TYPE_ID_PARAM,
  CHAT_WIDGET_NATIVE_WEBVIEW_ROUTE_PATHNAME,
  INTAKE_QUIZ_ROUTE_VISIT_TYPE_ID_PARAM,
  SCHEDULE_APPOINTMENT_TIME_PREFERENCE_ROUTE_PATHNAME,
} from 'router/routes/routePathnames';
import { SchedulePage, ServicesPage } from '@pages';
import { AppointmentTimePreferencePage } from '@pages/SchedulePage/AppointmentTimePreferencePage/AppointmentTimePreferencePage';
import { SandboxPage } from '@pages/DevTools/Sandbox/SandboxPage';
import dayjs from 'dayjs';
import { useApplicationStore } from '@data';
import { useInitMqtt } from '@lib/mqtt';
import { AppLayout } from '@router/components/AppLayout';
import {
  SERVICES_ROUTE_ID,
  SCHEDULE_ROUTE_ID,
  RESCHEDULE_ROUTE_ID,
  EMAIL_ROUTE_ID,
  PHONE_ROUTE_ID,
  CONFIRM_YOUR_NUMBER_ROUTE_ID,
  COMPLETE_PROFILE_ROUTE_ID,
  CREATE_PASSWORD_ROUTE_ID,
  COMPLETE_BOOKING_ROUTE_ID,
  BOOKING_CONFIRMATION_ROUTE_ID,
  ID_VERIFICATION_ROUTE_ID,
  DEV_TOOLS_SANDBOX_ROUTE_ID,
  APP_ROOT_ROUTE_ID,
  CHAT_WIDGET_NATIVE_WEBVIEW_ROUTE_ID,
  APPOINTMENT_TIME_PREFERENCE_ROUTE_ID,
  INTAKE_QUIZ_ROUTE_ID,
  REFERRAL_ROUTE_ID,
} from '../../constants/routeIds';
import { RouteError } from '@router/components/RouteError';
import { VISIT_TYPE_ID_ROUTE_PARAM } from '@router/routes/routeParams';

const ChatWidgetNativeWebview = lazy(() =>
  import('@pages/ChatWidgetNativeWebview').then((module) => ({
    default: module.ChatWidgetNativeWebview,
  })),
);

const VerifyIdentification = lazy(
  () => import('@pages/VerifyIdentificationPage'),
);
const IntakeQuizPage = lazy(() => import('@pages/IntakeQuizPage'));
const BookingConfirmationPage = lazy(() =>
  import('@pages/BookingConfirmationPage/BookingConfirmationPage').then(
    (module) => ({
      default: module.BookingConfirmationPage,
    }),
  ),
);
const CompleteBookingPage = lazy(() => import('@pages/CompleteBookingPage'));
const CompleteProfilePage = lazy(() =>
  import('@pages/CompleteProfilePage/CompleteProfilePage').then((module) => ({
    default: module.CompleteProfilePage,
  })),
);
const CreatePasswordPage = lazy(() => import('@pages/CreatePasswordPage'));
const EmailPage = lazy(() =>
  import('@pages/EmailPage/EmailPage').then((module) => ({
    default: module.EmailPage,
  })),
);
const PhonePage = lazy(() => import('@pages/PhonePage'));
const VerifyPhoneNumberPage = lazy(
  () => import('@pages/VerifyPhoneNumberPage'),
);
const ReferralPage = lazy(() =>
  import('@pages/ReferralPage/ReferralPage').then((module) => ({
    default: module.ReferralPage,
  })),
);
const initialize = () => {
  const sessionEnded = localStorage.getItem('sessionEnded');
  //allow for refresh to stay logged in
  if (
    !sessionEnded ||
    (sessionEnded && dayjs(sessionEnded).add(5, 'second').isBefore(dayjs()))
  ) {
    const resetState = useApplicationStore.getState().resetState;
    resetState();
  }
};

initialize();

const sentryCreatedBrowserRouter =
  wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreatedBrowserRouter([
  {
    id: APP_ROOT_ROUTE_ID,
    path: HOME_ROUTE_PATHNAME,
    element: <AppLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: HOME_ROUTE_PATHNAME,
        element: <RootRedirect />,
        loader: rootLoader,
      },
      {
        id: REFERRAL_ROUTE_ID,
        path: REFERRAL_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <ReferralPage />
          </SuspenseLoader>
        ),
        loader: referralLoader,
      },
      {
        id: SERVICES_ROUTE_ID,
        handle: {
          position: 1,
        },
        path: SERVICES_ROUTE_SERVICE_TYPE_ID_PARAM,
        element: <ServicesPage />,
        loader: servicesLoader,
      },
      {
        id: SCHEDULE_ROUTE_ID,
        handle: {
          position: 2,
        },
        path: SCHEDULE_ROUTE_VISIT_TYPE_ID_PARAM,
        element: <SchedulePage />,
        loader: scheduleLoader,
      },
      {
        id: RESCHEDULE_ROUTE_ID,
        handle: {
          position: 2,
        },
        path: RESCHEDULE_ROUTE_VISIT_TYPE_ID_PARAM,
        element: (
          <SuspenseLoader>
            <SchedulePage />
          </SuspenseLoader>
        ),
        loader: scheduleLoader,
      },
      {
        id: APPOINTMENT_TIME_PREFERENCE_ROUTE_ID,
        handle: {
          position: 2,
        },
        path: `${SCHEDULE_APPOINTMENT_TIME_PREFERENCE_ROUTE_PATHNAME}/:${VISIT_TYPE_ID_ROUTE_PARAM}`,
        element: <AppointmentTimePreferencePage />,
        loader: appointmentTimePreferenceLoader,
        shouldRevalidate: ({
          currentUrl,
          nextUrl,
        }: {
          currentUrl: URL;
          nextUrl: URL;
        }) => {
          return currentUrl.pathname !== nextUrl.pathname;
        },
      },
      {
        id: INTAKE_QUIZ_ROUTE_ID,
        handle: {
          position: 2,
        },
        path: INTAKE_QUIZ_ROUTE_VISIT_TYPE_ID_PARAM,
        element: (
          <SuspenseLoader>
            <IntakeQuizPage />
          </SuspenseLoader>
        ),
        loader: intakeQuizLoader,
      },
      {
        id: EMAIL_ROUTE_ID,
        handle: {
          position: 3,
        },
        path: EMAIL_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <EmailPage />
          </SuspenseLoader>
        ),
        loader: emailLoader,
      },
      {
        id: PHONE_ROUTE_ID,
        handle: {
          position: 4,
        },
        path: PHONE_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <PhonePage />
          </SuspenseLoader>
        ),
      },
      {
        id: CONFIRM_YOUR_NUMBER_ROUTE_ID,
        handle: {
          position: 5,
        },
        path: CONFIRM_YOUR_NUMBER_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <VerifyPhoneNumberPage />
          </SuspenseLoader>
        ),
        loader: confirmNumberLoader,
      },
      {
        id: COMPLETE_PROFILE_ROUTE_ID,
        handle: {
          position: 6,
        },
        path: COMPLETE_PROFILE_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <CompleteProfilePage />
          </SuspenseLoader>
        ),
        loader: completeProfileLoader,
      },
      {
        id: CREATE_PASSWORD_ROUTE_ID,
        handle: {
          position: 7,
        },
        path: CREATE_PASSWORD_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <CreatePasswordPage />
          </SuspenseLoader>
        ),
        loader: createPasswordLoader,
      },
      {
        id: COMPLETE_BOOKING_ROUTE_ID,
        handle: {
          position: 8,
        },
        path: COMPLETE_BOOKING_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <InactivityTimeout>
              <CompleteBookingPage />
            </InactivityTimeout>
          </SuspenseLoader>
        ),
        loader: completeBookingLoader,
      },
      {
        id: BOOKING_CONFIRMATION_ROUTE_ID,
        handle: {
          position: 9,
        },
        path: BOOKING_CONFIRMATION_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <InactivityTimeout>
              <BookingConfirmationPage />
            </InactivityTimeout>
          </SuspenseLoader>
        ),
        loader: bookingConfirmationLoader,
      },
      {
        id: ID_VERIFICATION_ROUTE_ID,
        handle: {
          position: 1,
        },
        path: ID_VERIFICATION_ROUTE_PATHNAME,
        element: (
          <SuspenseLoader>
            <InactivityTimeout>
              <VerifyIdentification />
            </InactivityTimeout>
          </SuspenseLoader>
        ),
        loader: idVerificationLoader,
      },
      {
        id: DEV_TOOLS_SANDBOX_ROUTE_ID,
        path: DEV_TOOLS_SANDBOX_ROUTE_PATHNAME,
        element: <SandboxPage />,
      },
      {
        id: CHAT_WIDGET_NATIVE_WEBVIEW_ROUTE_ID,
        path: CHAT_WIDGET_NATIVE_WEBVIEW_ROUTE_PATHNAME,
        element: <ChatWidgetNativeWebview />,
      },
      ...authRoutes,
      ...intakeFormRoutes,
      ...drugScreenRoutes,
      ...refillsRoutes,
    ],
  },
]);

export default function Router(): JSX.Element {
  useInitMqtt();

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem('sessionEnded', dayjs().toISOString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <RouterProvider router={router} fallbackElement={<FullScreenLoader />} />
  );
}
